/**
 * SecurityUpdateModal
 *
 * Daily reminder modal driven by useSecurityReminder + securityState.
 *   - soft mode → dismissible (close button, Esc, backdrop click all close)
 *   - hard mode → non-dismissible (only "Update now" is functional)
 *
 * Rendered directly by SecurityUpdateController, NOT through ModalContext.
 * This isolates it from other modal stack mutations so a stray closeModal()
 * cannot dismiss the hard-mode prompt.
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import SecurityCountdown from './SecurityCountdown';
import './SecurityUpdateModal.css';

const SecurityUpdateModal = ({
  currentVersion,
  minVersion,
  lockAt,
  securityNotice,
  dismissible,
  onUpdate,
  onDismiss,
}) => {
  const { t } = useI18n();

  // Esc key only closes when dismissible.
  useEffect(() => {
    if (!dismissible) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dismissible, onDismiss]);

  // Backdrop click only closes when dismissible.
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && dismissible) {
      onDismiss();
    }
  };

  let badge = null;
  if (securityNotice === 'critical') {
    badge = (
      <span
        className="security-update-modal__badge security-update-modal__badge--critical"
        data-testid="security-modal-badge-critical"
      >
        {t('SecurityUpdate.modal.criticalBadge', {}, 'Critical')}
      </span>
    );
  } else if (securityNotice === 'recommended') {
    badge = (
      <span
        className="security-update-modal__badge security-update-modal__badge--recommended"
        data-testid="security-modal-badge-recommended"
      >
        {t('SecurityUpdate.modal.recommendedBadge', {}, 'Recommended')}
      </span>
    );
  }

  return (
    <div
      className="security-update-modal__overlay"
      data-testid="security-update-modal"
      data-dismissible={dismissible ? 'true' : 'false'}
      role="dialog"
      aria-modal="true"
      aria-labelledby="security-update-modal-title"
      aria-describedby="security-update-modal-body"
      onClick={handleBackdropClick}
    >
      <div className="security-update-modal__card">
        <div className="security-update-modal__header">
          <h2
            id="security-update-modal-title"
            className="security-update-modal__title"
          >
            {t('SecurityUpdate.modal.title', {}, 'Security update required')}
          </h2>
          {badge}
        </div>
        <p
          id="security-update-modal-body"
          className="security-update-modal__body"
        >
          {t(
            'SecurityUpdate.modal.body',
            { currentVersion, minVersion },
            `Your client (v${currentVersion}) is below the required security minimum (v${minVersion}). Please update now.`,
          )}
        </p>
        <SecurityCountdown
          lockAt={lockAt}
          className="security-update-modal__countdown"
        />
        <div className="security-update-modal__actions">
          <button
            type="button"
            className="security-update-modal__button security-update-modal__button--primary"
            data-testid="security-modal-update-now"
            onClick={onUpdate}
          >
            {t('SecurityUpdate.modal.updateNow', {}, 'Update now')}
          </button>
          {dismissible && (
            <button
              type="button"
              className="security-update-modal__button security-update-modal__button--secondary"
              data-testid="security-modal-remind-later"
              onClick={onDismiss}
            >
              {t('SecurityUpdate.modal.remindLater', {}, 'Remind me later')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

SecurityUpdateModal.propTypes = {
  currentVersion: PropTypes.string.isRequired,
  minVersion: PropTypes.string,
  lockAt: PropTypes.string,
  securityNotice: PropTypes.oneOf(['critical', 'recommended']),
  dismissible: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default SecurityUpdateModal;
